import React from "react";
import { Link } from "react-router-dom";
import { FaPenNib, FaPalette, FaRulerCombined, FaArrowRight, FaCheckCircle, FaBolt } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import SEO from "../../components/common/SEO";
import PageSection from "../../components/common/PageSection";
import Breadcrumb from "../../components/common/Breadcrumb";

// Decorative header art for the Pen Path card - an anchor-point bezier curve.
const PenPathVisual = () => (
  <svg viewBox="0 0 400 180" fill="none" className="absolute inset-0 w-full h-full">
    <path
      d="M40 140 C 110 30, 180 30, 210 100 S 320 190, 360 60"
      stroke="url(#penPathGradient)"
      strokeWidth="2.5"
      strokeDasharray="6 6"
      className="opacity-70"
    />
    <path
      d="M40 140 C 110 30, 180 30, 210 100 S 320 190, 360 60"
      stroke="#E31C25"
      strokeWidth="2"
      strokeLinecap="round"
      pathLength="1"
      strokeDasharray="1"
      strokeDashoffset="0"
    />
    <defs>
      <linearGradient id="penPathGradient" x1="0" y1="0" x2="400" y2="0">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
        <stop offset="100%" stopColor="#E31C25" stopOpacity="0.6" />
      </linearGradient>
    </defs>
    <line x1="210" y1="100" x2="270" y2="70" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="1.5" />
    <circle cx="270" cy="70" r="3.5" fill="#ffffff" fillOpacity="0.6" />
    {[
      [40, 140],
      [210, 100],
      [360, 60],
    ].map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="6" fill="#0b0b0f" stroke="#E31C25" strokeWidth="2" />
    ))}
  </svg>
);

// Decorative header art for the Arvix Color card - palette swatches + a contrast chip.
const ColorToolVisual = () => (
  <div className="absolute inset-0 flex items-center justify-center gap-3">
    {["#E31C25", "#FFB020", "#39A0ED", "#2ECC71", "#8E5CF7"].map((hex, i) => (
      <div
        key={hex}
        className="rounded-2xl shadow-lg ring-1 ring-white/10 transition-transform duration-500 group-hover:-translate-y-1"
        style={{
          background: hex,
          width: 36,
          height: i === 2 ? 92 : 64,
          transitionDelay: `${i * 40}ms`,
          alignSelf: "center",
        }}
      />
    ))}
    <div className="ml-2 px-3 py-2 rounded-xl bg-white/95 shadow-lg flex items-center gap-1.5">
      <span className="text-xs font-bold text-designer-dark">Aa</span>
      <span className="text-[9px] font-mono tracking-wide text-emerald-600 font-bold">AA ✓</span>
    </div>
  </div>
);

// Decorative header art for the Measurix card - a print sheet with bleed & safe-zone guides.
const MeasurixVisual = () => (
  <svg viewBox="0 0 400 180" fill="none" className="absolute inset-0 w-full h-full">
    {/* Bleed box */}
    <rect
      x="140"
      y="26"
      width="120"
      height="128"
      rx="2"
      stroke="#E31C25"
      strokeWidth="1.5"
      strokeDasharray="5 5"
      className="opacity-80"
    />
    {/* Trim / finish size */}
    <rect x="150" y="36" width="100" height="108" rx="1.5" fill="#ffffff" fillOpacity="0.08" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="1.5" />
    {/* Safe zone */}
    <rect x="162" y="48" width="76" height="84" rx="1" stroke="#39A0ED" strokeWidth="1.25" strokeDasharray="4 4" className="opacity-70" />
    {/* Content placeholder lines */}
    {[62, 74, 86, 98].map((y, i) => (
      <rect key={y} x="172" y={y} width={i === 3 ? 32 : 56 - i * 6} height="4" rx="2" fill="#ffffff" fillOpacity="0.28" />
    ))}
    {/* Horizontal dimension line */}
    <line x1="150" y1="166" x2="250" y2="166" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    <line x1="150" y1="161" x2="150" y2="171" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    <line x1="250" y1="161" x2="250" y2="171" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    {/* Vertical dimension line */}
    <line x1="128" y1="36" x2="128" y2="144" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    <line x1="123" y1="36" x2="133" y2="36" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    <line x1="123" y1="144" x2="133" y2="144" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
    {/* Labels */}
    <text x="200" y="178" textAnchor="middle" fill="#ffffff" fillOpacity="0.7" fontSize="10" fontFamily="'Space Mono', monospace">
      210 mm
    </text>
    <text x="118" y="94" textAnchor="middle" fill="#ffffff" fillOpacity="0.7" fontSize="10" fontFamily="'Space Mono', monospace" transform="rotate(-90 118 94)">
      297 mm
    </text>
    <text x="286" y="34" fill="#E31C25" fillOpacity="0.9" fontSize="9" fontFamily="'Space Mono', monospace" letterSpacing="1">
      BLEED 3mm
    </text>
    <text x="286" y="52" fill="#39A0ED" fillOpacity="0.9" fontSize="9" fontFamily="'Space Mono', monospace" letterSpacing="1">
      SAFE ZONE
    </text>
    <text x="286" y="70" fill="#ffffff" fillOpacity="0.55" fontSize="9" fontFamily="'Space Mono', monospace" letterSpacing="1">
      300 DPI
    </text>
    {/* Ruler ticks along the top */}
    {Array.from({ length: 21 }).map((_, i) => (
      <line
        key={i}
        x1={40 + i * 16}
        y1="12"
        x2={40 + i * 16}
        y2={i % 5 === 0 ? 22 : 17}
        stroke="#ffffff"
        strokeOpacity={i % 5 === 0 ? 0.4 : 0.2}
        strokeWidth="1"
      />
    ))}
    <line x1="40" y1="12" x2="360" y2="12" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="1" />
  </svg>
);

const TOOLS = [
  {
    id: "pen-path",
    name: "Pen Path",
    tagline: "Illustrator Pen Tool Practice",
    description:
      "Master Adobe Illustrator's pen tool through 50 progressive Bézier drills, from straight corners to smooth curves, with real-time accuracy scoring and guide overlays.",
    url: "https://arvix-pentool.vercel.app/",
    icon: FaPenNib,
    visual: PenPathVisual,
    theme: "dark",
    features: ["50 skill levels", "Real-time accuracy scoring", "Anchor & handle drills"],
  },
  {
    id: "arvix-color",
    name: "Arvix Color",
    tagline: "Daily Color Toolkit for Designers",
    description:
      "Pick harmonious color schemes in seconds: analogous, complementary, triadic and more. Check WCAG contrast, and save palettes you can reuse on every project.",
    url: "https://arvix-color.vercel.app/",
    icon: FaPalette,
    visual: ColorToolVisual,
    theme: "light",
    features: ["Color harmony modes", "WCAG contrast checker", "Save & reuse palettes"],
  },
  {
    id: "measurix",
    name: "Measurix",
    tagline: "Print & Stationery Size Reference",
    description:
      "Every print size in one place: paper, envelopes, visiting cards, banners, bags, boxes and notepads, with finish size, design canvas, bleed, safe zone and print specs for each.",
    url: "https://measurix-1m1n.vercel.app/",
    icon: FaRulerCombined,
    visual: MeasurixVisual,
    theme: "dark",
    features: ["Bleed & safe zone specs", "Search any size instantly", "Paper, banner, box & more"],
  },
];

const HERO_POINTS = [
  { icon: FaBolt, label: "3 free tools" },
  { icon: FaCheckCircle, label: "No signup needed" },
  { icon: HiSparkles, label: "Built in-house" },
];

const ToolRow = ({ tool, index }) => {
  const Icon = tool.icon;
  const Visual = tool.visual;
  const domain = tool.url.replace("https://", "").replace(/\/$/, "");
  const number = String(index + 1).padStart(2, "0");
  const flip = index % 2 === 1;

  return (
    <a
      href={tool.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group grid lg:grid-cols-2 bg-white rounded-[2rem] shadow-xl hover:shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2"
      aria-label={`Launch ${tool.name}, free tool, opens in a new tab`}
    >
      {/* Visual side */}
      <div
        className={`relative h-60 sm:h-72 lg:h-auto lg:min-h-[380px] overflow-hidden ${
          flip ? "lg:order-2" : ""
        } ${
          tool.theme === "dark"
            ? "bg-[#0b0b0f]"
            : "bg-gradient-to-br from-gray-50 to-red-50/40"
        }`}
      >
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,.8) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.8) 1px,transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <Visual />
        <span
          aria-hidden="true"
          className="absolute top-4 left-5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold tracking-[0.18em] shadow-lg"
        >
          FREE
        </span>
        <span
          aria-hidden="true"
          className={`absolute bottom-2 right-5 font-display font-bold text-7xl xl:text-8xl leading-none select-none ${
            tool.theme === "dark" ? "text-white/10" : "text-designer-dark/10"
          }`}
        >
          {number}
        </span>
      </div>

      {/* Content side */}
      <div className={`flex flex-col justify-center p-7 sm:p-10 xl:p-14 ${flip ? "lg:order-1" : ""}`}>
        <p className="text-xs font-bold tracking-[0.25em] text-designer-primary uppercase mb-4">
          Tool {number} &mdash; {tool.tagline}
        </p>
        <div className="flex items-center gap-4 mb-5">
          <div className="shrink-0 rounded-2xl bg-gradient-to-br from-designer-primary to-red-600 text-white p-4 text-2xl shadow-lg shadow-red-500/25 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500">
            <Icon aria-hidden="true" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-designer-dark leading-none">
            {tool.name}
          </h2>
        </div>

        <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7 max-w-lg">
          {tool.description}
        </p>

        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
          {tool.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-[15px] text-gray-700 font-medium">
              <FaCheckCircle className="w-[18px] h-[18px] mt-0.5 shrink-0 text-designer-primary" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="inline-flex items-center gap-2.5 bg-designer-primary group-hover:bg-red-600 text-white font-bold text-base px-7 py-3.5 rounded-xl shadow-lg shadow-red-500/25 transition-all duration-300">
            Launch {tool.name}
            <FaArrowRight className="w-4 h-4 transform group-hover:translate-x-1.5 transition-transform duration-300" aria-hidden="true" />
          </span>
          <span className="text-sm text-gray-400 font-medium">
            {domain} &middot; Free forever
          </span>
        </div>
      </div>
    </a>
  );
};

const Tools = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Free Design Tools by Arvix Creative",
    description:
      "Free web tools built by Arvix Creative for designers: an Illustrator pen tool trainer, a color palette & contrast toolkit, and a print size reference.",
    url: "https://arvixcreative.com/tools",
  };

  return (
    <>
      <SEO
        title="Our Tools - Free Design Tools by Arvix Creative"
        description="Free web tools built by Arvix Creative for the design community: Pen Path, an Illustrator pen tool trainer, Arvix Color, a daily color toolkit, and Measurix, a print & stationery size reference. No signup required."
        keywords="arvix tools, free design tools, illustrator pen tool practice, color palette generator, WCAG contrast checker, print size reference, bleed and safe zone, arvix pentool, arvix color, measurix"
        url="https://arvixcreative.com/tools"
        structuredData={structuredData}
      />
      <div className="pt-24 pb-6 bg-white">
        <Breadcrumb items={[{ label: "Our Tools" }]} />
      </div>

      <PageSection className="pt-8 pb-16 md:pb-24 min-h-screen" gradient="none" overflowHidden={false}>
        <div className="w-full relative z-10">
          {/* Header */}
          <div className="text-center mb-12 md:mb-14 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-1.5 mb-5 px-4 py-1.5 rounded-full bg-red-100/50 border border-red-200/50">
              <HiSparkles className="w-3.5 h-3.5 text-designer-primary" />
              <p className="text-xs font-bold tracking-[0.25em] text-designer-primary uppercase">Our Tools</p>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-5">
              Free Tools, Built{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-designer-primary to-red-600">
                In-House
              </span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-7">
              Beyond client work, we build small web tools for the design community.
              Open one and start working, no account, no paywall.
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" aria-label="Tool highlights">
              {HERO_POINTS.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200/80 shadow-sm text-sm font-semibold text-gray-600"
                >
                  <Icon className="w-3.5 h-3.5 text-designer-primary" aria-hidden="true" />
                  {label}
                </li>
              ))}
            </ul>
          </div>

          {/* Tools rows */}
          <div className="flex flex-col gap-8 md:gap-12">
            {TOOLS.map((tool, index) => (
              <ToolRow key={tool.id} tool={tool} index={index} />
            ))}
          </div>

          {/* CTA band */}
          <div className="mt-12 md:mt-16">
            <div className="relative overflow-hidden rounded-3xl bg-designer-dark text-white px-6 py-10 sm:px-10 md:px-12 md:py-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div
                aria-hidden="true"
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-designer-primary/30 blur-3xl"
              />
              <div
                aria-hidden="true"
                className="absolute -bottom-28 -left-16 w-72 h-72 rounded-full bg-designer-primary/20 blur-3xl"
              />
              <div className="relative flex-1">
                <p className="text-xs font-bold tracking-[0.25em] text-designer-primary uppercase mb-3">
                  Built for designers
                </p>
                <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                  Have a workflow that needs its own tool?
                </h2>
                <p className="mt-3 text-white/70 leading-relaxed max-w-xl">
                  These started as our own studio shortcuts. Tell us what slows you down and
                  we might build it next, free for everyone.
                </p>
              </div>
              <div className="relative flex flex-wrap items-center gap-3 shrink-0">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-designer-primary text-white text-sm font-bold shadow-lg shadow-red-900/40 hover:bg-red-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Suggest a tool
                  <FaArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
                <a
                  href="mailto:arvixcreative@gmail.com"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/25 text-white text-sm font-bold hover:border-white hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Email us
                </a>
              </div>
            </div>
            <p className="mt-6 text-center text-sm text-gray-400">
              Built by Arvix Creative. Free forever, no account needed.
            </p>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default Tools;
